




import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Namespace } from "@/store/types";
import { Action, Getter, Mutation } from "vuex-class";
import ChannelChange from "@/components/channelChange/ChannelChange.vue";
import {
  ChannelChangeAction,
  ChannelChangeGetter,
  ChannelChangeMutation
} from "@/components/channelChange/types";
import { PlayerGetter } from "@/components/player/types";
import { RouteName } from "@/router/types";
import apiStorageInterface from "@/services/apiStorageInterface";
import {
  OverlayAction,
  OverlayGetter,
  OverlayMutation
} from "@/components/overlay/types";
import { Player as ClapprPlayer } from "clappr";
import { AlertAction, AlertState, AlertType } from "@/components/alert/types";
import dayjs from "dayjs";
import Duration from "dayjs/plugin/duration";
import { Episode, Movie } from "@/services/apiService/api.types";
import { EventBus } from "@/main";
import { PlayerControlsEnum } from "@/helpers/constants";

@Component({
  components: { ChannelChange }
})
export default class Player extends Vue {
  @Prop() readonly index?: number;
  @Prop() readonly swiperActiveIndex!: number;

  @Action(ChannelChangeAction.UpdateSource, {
    namespace: Namespace.ChannelChange
  })
  updateSource!: (src: string) => Promise<void>;

  @Action(ChannelChangeAction.UpdateOffset, {
    namespace: Namespace.ChannelChange
  })
  updateOffset!: (offset: number) => Promise<void>;

  @Action(OverlayAction.UpdateChannelEpg, {
    namespace: Namespace.Overlay
  })
  updateChannelEpg!: () => Promise<void>;

  @Action(AlertAction.UpdateAlert, { namespace: Namespace.Alert })
  newAlert!: (ChannelChangeGetter: AlertState) => Promise<void>;

  @Action(OverlayAction.UpdateActiveVodItemProgressDuration, {
    namespace: Namespace.Overlay
  })
  updateActiveVodItemProgressDuration!: (
    playbackTimestamp: number | null
  ) => void;

  @Mutation(ChannelChangeMutation.SetChannelId, {
    namespace: Namespace.ChannelChange
  })
  setChannelId!: (channelId: number) => Promise<void>;

  @Mutation(OverlayMutation.SetActiveVodItemDuration, {
    namespace: Namespace.Overlay
  })
  setActiveVodItemDuration!: (duration: number | null) => void;

  @Mutation(OverlayMutation.SetActiveTranslationId, {
    namespace: Namespace.Overlay
  })
  setActiveTranslationId!: (langId: number | null) => void;

  @Getter(ChannelChangeGetter.GetSource, { namespace: Namespace.ChannelChange })
  source!: string;

  @Getter(ChannelChangeGetter.GetOffset, { namespace: Namespace.ChannelChange })
  offset!: string;

  @Getter(PlayerGetter.GetPause, { namespace: Namespace.Player })
  getPause!: boolean;

  @Getter(OverlayGetter.GetActiveVodItemProgressDuration, {
    namespace: Namespace.Overlay
  })
  getActiveVodItemProgressDuration!: number | null;

  @Getter(OverlayGetter.GetActiveVodItem, {
    namespace: Namespace.Overlay
  })
  getActiveVodItem!: Movie | Episode;

  get fullSource(): string {
    if (this.vodUrl && this.vodUrl !== "") {
      return this.vodUrl;
    }

    return this.source + "&offset=" + this.offset;
  }

  player: ClapprPlayer | null = null;

  retries = 0;

  vodProgressCounter = 0; //reference to interval for updating VoD progress

  async mounted(): Promise<void> {
    this.createPlayer();
    if (this.source === "" && !this.getActiveVodItem) {
      try {
        const channelList = await apiStorageInterface.fetchCustomChannelList();

        await Promise.all([
          this.updateSource(channelList[0].videoLive),
          this.updateOffset(0),
          this.setChannelId(channelList[0].id),
          this.updateChannelEpg()
        ]);
      } catch (e) {
        throw Error(e);
      }
    }
    EventBus.$on(PlayerControlsEnum.Start, () => {
      this.player?.configure({
        source: this.fullSource
      });
    });
    EventBus.$on(PlayerControlsEnum.End, () => {
      this.player?.seek(this.player?.getDuration());
    });
    EventBus.$on(PlayerControlsEnum.Rewind, (data: unknown) => {
      if (typeof data !== "undefined") {
        this.player?.seek(
          this.getActiveVodItemProgressDuration! - (data as number)
        );
      }
    });
    EventBus.$on(PlayerControlsEnum.FastForward, (data: unknown) => {
      if (typeof data !== "undefined") {
        this.player?.seek(
          this.getActiveVodItemProgressDuration! + (data as number)
        );
      }
    });
  }

  createPlayer(): void {
    if (this.player) {
      // this.reloadPlayer(this.player);
    } else {
      this.player = new ClapprPlayer({
        source: this.fullSource,
        poster: this.getActiveVodItem
          ? this.getActiveVodItem.media?.url
          : undefined,
        width: "100%",
        height: "100%",
        autoPlay: true,
        parentId: "#player" + this.playerIndex,
        mute: false,
        loop: true,
        useHardwareVideoDecoder: false,
        playback: {
          hlsjsConfig: {
            maxBufferLength: 5
          }
        }
      });

      this.player.getPlugin("click_to_pause").disable();

      this.player?.on("playererror", () => {
        this.retries++;
        if (this.retries > 5) {
          this.newAlert({
            show: true,
            message: "The stream has failed to load, redirecting to Login",
            alertType: AlertType.Danger
          });
          setTimeout(() => {
            this.player?.destroy();
            this.$router.push({ name: RouteName.Login });
          }, 2500);
        }
      });

      this.player?.on("play", () => {
        if (this.index !== this.swiperActiveIndex) {
          this.player?.pause();
        }

        if (this.getActiveVodItem && this.getActiveVodItem.id !== 0)
          this.updateOverlayPlaybackInfo();
      });

      this.player
        .getPlugin("closed_captions")
        .container.on("container:subtitle:changed", (track: { id: number }) => {
          this.setActiveTranslationId(track.id);
        });
    }
  }

  updateOverlayPlaybackInfo(): void {
    if (this.player && this.vodUrl !== "") {
      dayjs.extend(Duration);
      const duration = dayjs
        .duration(Math.floor(this.player.getDuration()), "s")
        .asSeconds();
      this.setActiveVodItemDuration(duration);
      clearInterval(this.vodProgressCounter);
      this.vodProgressCounter = setInterval(() => {
        const progress = dayjs
          .duration(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            Math.floor(this.player?.getCurrentTime()),
            "s"
          )
          .asSeconds();

        this.updateActiveVodItemProgressDuration(progress);
      }, 1000);
    }
  }

  @Watch("source")
  @Watch("offset")
  handleSourceUpdate(): void {
    this.player?.configure({
      source: this.fullSource
    });
  }

  get playerIndex(): string {
    return this.index != undefined ? this.index.toString() : "";
  }

  @Watch("getPause")
  handlePause(newVal: boolean): void {
    if (!newVal) {
      const prevPlaybackProgress = this.getActiveVodItemProgressDuration; //prevents player from resetting progress before seek occurs
      this.player?.play();
      if (prevPlaybackProgress) {
        setTimeout(() => {
          this.player?.seek(prevPlaybackProgress!);
        }, 500);
      }
      return;
    }

    this.player?.pause();
  }

  //dynamically create/destroy player based of whether slide is currently visible
  @Watch("swiperActiveIndex")
  handleSideBarOpened(
    newVal: number | undefined,
    oldVal: number | undefined
  ): void {
    if (oldVal === this.index) {
      this.player?.pause();
    }

    if (newVal === this.index) {
      this.player?.play();
    }
  }

  get vodUrl(): string {
    return this.getActiveVodItem ? this.getActiveVodItem.vod_hls! : "";
  }

  beforeDestroy(): void {
    clearInterval(this.vodProgressCounter);
    this.player?.destroy();
    EventBus.$off(PlayerControlsEnum.Start);
  }
}
